import api from './api'

/**
 * Conversation Service
 * Handles all conversation-related API calls
 */
class ConversationService {
  /**
   * Create new conversation
   * POST v1/{club}/conversations
   */
  async createConversation(clubUuid: string, data: {
    participant_uuids: string[]
    is_group: boolean
    name?: string
  }): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/conversations`, data)
    return response.data
  }

  /**
   * Get conversations list
   * GET v1/{club}/conversations
   */
  async getConversations(clubUuid: string, params?: {
    limit?: number
    offset?: number
  }): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/conversations`, { params })
    return response.data
  }

  /**
   * Get conversation details
   * GET v1/{club}/conversations/{conversation}
   */
  async getConversation(clubUuid: string, conversationUuid: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/conversations/${conversationUuid}`)
    return response.data
  }

  /**
   * Get conversation participants
   * GET v1/{club}/conversations/{conversation}/participants
   */
  async getParticipants(clubUuid: string, conversationUuid: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/conversations/${conversationUuid}/participants`)
    return response.data
  }

  /**
   * Get available persons to add to conversation
   * GET v1/{club}/conversations/{conversation}/available-persons
   */
  async getAvailablePersons(clubUuid: string, conversationUuid: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/conversations/${conversationUuid}/available-persons`)
    return response.data
  }

  /**
   * Add participants to conversation
   * POST v1/{club}/conversations/{conversation}/participants
   */
  async addParticipants(clubUuid: string, conversationUuid: string, data: {
    participant_uuids: string[]
  }): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/conversations/${conversationUuid}/participants`, data)
    return response.data
  }

  /**
   * Remove participant from conversation
   * DELETE v1/{club}/conversations/{conversation}/participants/{participant}
   */
  async removeParticipant(clubUuid: string, conversationUuid: string, participantUuid: string): Promise<any> {
    const response = await api.delete(`v1/${clubUuid}/conversations/${conversationUuid}/participants/${participantUuid}`)
    return response.data
  }

  /**
   * Update group name
   * PUT v1/{club}/conversations/{conversation}/name
   */
  async updateGroupName(clubUuid: string, conversationUuid: string, data: {
    name: string
  }): Promise<any> {
    const response = await api.put(`v1/${clubUuid}/conversations/${conversationUuid}/name`, data)
    return response.data
  }

  /**
   * Get messages from conversation
   * GET v1/{club}/conversations/{conversation}/messages
   */
  async getMessages(clubUuid: string, conversationUuid: string, params?: {
    limit?: number
    offset?: number
    before_uuid?: string
  }): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/conversations/${conversationUuid}/messages`, { params })
    return response.data
  }

  /**
   * Send message in conversation
   * POST v1/{club}/conversations/{conversation}/messages
   */
  async sendMessage(clubUuid: string, conversationUuid: string, data: {
    content: string
    message_type?: string
  } | FormData): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/conversations/${conversationUuid}/messages`, data)
    return response.data
  }

  /**
   * Mark conversation as read
   * POST v1/{club}/conversations/{conversation}/mark-as-read
   */
  async markAsRead(clubUuid: string, conversationUuid: string): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/conversations/${conversationUuid}/mark-as-read`)
    return response.data
  }

  /**
   * Get images from conversation with pagination
   * GET v1/{club}/conversations/{conversation}/images
   */
  async getConversationImages(clubUuid: string, conversationUuid: string, params?: {
    limit?: number
    offset?: number
    after_timestamp?: string
    before_timestamp?: string
  }): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/conversations/${conversationUuid}/images`, { params })
    return response.data
  }

  /**
   * Get images around specific image (for modal opening)
   * GET v1/{club}/conversations/{conversation}/images/{image}/around
   */
  async getImagesAroundImage(clubUuid: string, conversationUuid: string, imageUuid: string, params?: {
    before?: number
    after?: number
  }): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/conversations/${conversationUuid}/images/${imageUuid}/around`, { params })
    return response.data
  }

  /**
   * Get documents from conversation with pagination
   * GET v1/{club}/conversations/{conversation}/documents
   */
  async getConversationDocuments(clubUuid: string, conversationUuid: string, params?: {
    limit?: number
    offset?: number
  }): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/conversations/${conversationUuid}/documents`, { params })
    return response.data
  }

  /**
   * Get unread conversations count
   * GET v1/{club}/conversations/unread
   */
  async getUnreadCount(clubUuid: string): Promise<number> {
    const response = await api.get(`v1/${clubUuid}/conversations/unread`)
    return response.data.unread_count || 0
  }

  /**
   * Get unread conversations data (count + UUIDs)
   * GET v1/{club}/conversations/unread
   */
  async getUnreadData(clubUuid: string): Promise<{ unread_count: number, conversation_uuids: string[] }> {
    const response = await api.get(`v1/${clubUuid}/conversations/unread`)
    return {
      unread_count: response.data.unread_count || 0,
      conversation_uuids: response.data.conversation_uuids || []
    }
  }
}

export default new ConversationService()
